import { useMemo, useState } from 'react';
import type { Stage, ChecklistCategory, UseChecklistResult } from '../types';
import { Mascot } from './mascots';
import { MascotRow } from './MascotRow';
import { DetailDisclosure } from './Expandable';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import styles from '../App.module.css';

export type ChecklistEditor = {
  hideItem: (stageId: string, itemId: string) => void;
  removeCustomItem: (stageId: string, itemId: string) => void;
  renameItem: (stageId: string, itemId: string, text: string, isCustom: boolean) => void;
  addItem: (input: {
    stageId: string;
    text: string;
    category: ChecklistCategory;
    note?: string;
  }) => void;
  restoreStageDefaults: (stageId: string) => void;
};

interface ChecklistProps {
  stage: Stage;
  checklist: UseChecklistResult;
  editor?: ChecklistEditor;
}

function percent(p: number): string {
  return `${Math.round(p * 100)}%`;
}

const CATEGORIES: ChecklistCategory[] = [
  'body',
  'mind',
  'hospital',
  'admin',
  'shopping',
  'life',
  'partner',
];

export function Checklist({ stage, checklist, editor }: ChecklistProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [filter, setFilter] = useState<ChecklistCategory | 'all'>('all');
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [draftCategory, setDraftCategory] = useState<ChecklistCategory>('life');

  const items = useMemo(() => {
    if (filter === 'all') return stage.checklist;
    return stage.checklist.filter((item) => item.category === filter);
  }, [filter, stage.checklist]);

  const categories = useMemo(() => {
    const set = new Set(stage.checklist.map((item) => item.category));
    return Array.from(set) as ChecklistCategory[];
  }, [stage.checklist]);

  const done = useMemo(
    () => stage.checklist.filter((item) => checklist.isChecked(stage.id, item.id)).length,
    [stage.checklist, stage.id, checklist]
  );
  const total = stage.checklist.length;
  const progress = total === 0 ? 0 : done / total;

  function handleDelete(itemId: string, isCustom: boolean) {
    if (!editor) return;
    if (!window.confirm(ui.checklist.deleteConfirm)) return;
    if (isCustom) editor.removeCustomItem(stage.id, itemId);
    else editor.hideItem(stage.id, itemId);
  }

  function handleRename(itemId: string, current: string, isCustom: boolean) {
    if (!editor) return;
    const next = window.prompt(ui.checklist.editItem, current);
    if (next == null) return;
    editor.renameItem(stage.id, itemId, next, isCustom);
  }

  function handleAdd() {
    if (!editor || !draftText.trim()) return;
    editor.addItem({
      stageId: stage.id,
      text: draftText,
      category: draftCategory,
    });
    setDraftText('');
  }

  return (
    <div className={styles.checklist}>
      <div className={styles.checklistHeader}>
        <div className={styles.checklistProgress}>
          <span className={styles.checklistProgressLabel}>{ui.checklist.progress}</span>
          <span className={styles.checklistProgressValue}>{percent(progress)}</span>
          <span className={styles.checklistProgressCount}>
            {ui.checklist.completedCount
              .replace('{done}', String(done))
              .replace('{total}', String(total))}
          </span>
        </div>
        <div className={styles.progressBar} aria-hidden="true">
          <div className={styles.progressFill} style={{ width: percent(progress) }} />
        </div>
      </div>

      {editor ? (
        <div className={styles.editToolbar}>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? ui.checklist.doneEdit : ui.checklist.editMode}
          </button>
          {editing ? (
            <button
              type="button"
              className={styles.resetButton}
              onClick={() => {
                if (window.confirm(ui.checklist.restoreConfirm)) {
                  editor.restoreStageDefaults(stage.id);
                }
              }}
            >
              {ui.checklist.restoreDefaults}
            </button>
          ) : null}
        </div>
      ) : null}

      {progress >= 1 && (
        <div className={styles.celebration} role="status">
          <MascotRow
            items={[
              { id: 'star', size: 30 },
              { id: 'momo', size: 44 },
              { id: 'star', size: 30 },
            ]}
          />
          <p className={styles.celebrationText}>{ui.checklist.celebration}</p>
        </div>
      )}

      <div className={styles.categoryFilter} role="group" aria-label={ui.checklist.filterAriaLabel}>
        <button
          type="button"
          className={`${styles.categoryChip} ${filter === 'all' ? styles.categoryChipActive : ''}`}
          onClick={() => setFilter('all')}
          aria-pressed={filter === 'all'}
        >
          {ui.checklist.all}
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`${styles.categoryChip} ${filter === cat ? styles.categoryChipActive : ''}`}
            onClick={() => setFilter(cat)}
            aria-pressed={filter === cat}
          >
            {ui.checklist.categoryLabels[cat]}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className={styles.emptyState}>
          <Mascot id="sleep" size={56} />
          <p className={styles.emptyStateText}>{ui.checklist.empty}</p>
        </div>
      ) : (
        <ul className={styles.checklistList}>
          {items.map((item) => {
            const checked = checklist.isChecked(stage.id, item.id);
            return (
              <li
                key={item.id}
                className={`${styles.checklistItem} ${checked ? styles.checklistItemChecked : ''}`}
              >
                <label className={styles.checklistLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkboxInput}
                    checked={checked}
                    onChange={() => checklist.toggle(stage.id, item.id)}
                  />
                  <span className={styles.checkboxVisual} aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className={styles.itemTextWrap}>
                    <span className={styles.itemText}>
                      {item.text}
                      {item.custom ? (
                        <span className={styles.customBadge}>{ui.checklist.customBadge}</span>
                      ) : null}
                    </span>
                    <span className={styles.itemCategory}>
                      {ui.checklist.categoryLabels[item.category]}
                    </span>
                    {item.note ? (
                      <DetailDisclosure detail={item.note} className={styles.itemDetail} />
                    ) : null}
                  </span>
                </label>
                {editing && editor ? (
                  <div className={styles.itemEditActions}>
                    <button
                      type="button"
                      className={styles.tinyButton}
                      onClick={() => handleRename(item.id, item.text, Boolean(item.custom))}
                    >
                      {ui.checklist.editItem}
                    </button>
                    <button
                      type="button"
                      className={styles.tinyButtonDanger}
                      onClick={() => handleDelete(item.id, Boolean(item.custom))}
                    >
                      {ui.checklist.deleteItem}
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {editing && editor ? (
        <div className={styles.addItemBox}>
          <p className={styles.addItemTitle}>{ui.checklist.addItem}</p>
          <label className={styles.onboardingField}>
            <span>{ui.checklist.categoryLabel}</span>
            <select
              className={styles.onboardingInput}
              value={draftCategory}
              onChange={(e) => setDraftCategory(e.target.value as ChecklistCategory)}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {ui.checklist.categoryLabels[cat]}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.addItemRow}>
            <input
              className={styles.onboardingInput}
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              placeholder={ui.checklist.addPlaceholder}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <button
              type="button"
              className={styles.primaryButton}
              disabled={!draftText.trim()}
              onClick={handleAdd}
            >
              {ui.checklist.addButton}
            </button>
          </div>
        </div>
      ) : null}

      <div className={styles.checklistActions}>
        <button
          type="button"
          className={styles.resetButton}
          onClick={() => {
            if (window.confirm(ui.checklist.resetConfirm(stage.title))) {
              checklist.resetStage(stage.id);
            }
          }}
        >
          {ui.checklist.resetStage}
        </button>
        <button type="button" className={styles.resetButton} onClick={checklist.resetAll}>
          {ui.checklist.resetAll}
        </button>
      </div>
    </div>
  );
}
